/**
 * The partners screen (P1-BR7, BR-01, BR-03).
 *
 * The one screen four partners will open to check something about each other,
 * so it shows all four figures side by side and nothing is derived here:
 * capital, drawings, net position and ownership all arrive computed by the
 * server (BR-01).
 *
 * The percentages sum to exactly 100 because the allocator puts the rounding
 * remainder on the last share (MONEY-5). On this screen a total of 99.99 does
 * not read as a rounding artefact — it reads as somebody being short-changed.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useTranslation } from '../../i18n/useTranslation.js';
import { formatMoney, isNegativeAmount } from '../../lib/currency.js';
import { PartnerLedgerPanel } from './PartnerLedgerPanel.js';
import { usePartners } from './api.js';

export function PartnersScreen(): JSX.Element {
  const { t } = useTranslation();
  const { data: partners = [], isLoading, isError, refetch } = usePartners();
  /* Which partner's entries are open. The totals on this screen are sums, and
     until now there was no way to see what they were made of — so a figure a
     partner disagreed with had nothing behind it to check. */
  const [openId, setOpenId] = useState<number | null>(null);

  return (
    <div className="page-container">
      <h1 className="py-4 text-xl font-semibold text-gray-800">{t('finance.partners.title')}</h1>

      {/* Every figure on this screen is derived from capital_in and drawing
          entries (BR-01). Without a way to record one they all read zero, so
          the way to record one belongs here. */}
      <Link
        to="/partners/money"
        className="mb-4 flex min-h-touch items-center justify-between rounded-xl bg-green-700 px-4 py-3 text-white"
      >
        <span className="text-sm font-medium">{t('partnerMoney.title')}</span>
        <span aria-hidden="true">→</span>
      </Link>

      {isLoading && (
        <p role="status" className="py-8 text-center text-gray-500">
          {t('app.loading')}
        </p>
      )}

      {isError && (
        <div role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          <p className="mb-2">{t('error.network')}</p>
          <button type="button" onClick={() => void refetch()} className="min-h-touch font-medium underline">
            {t('app.retry')}
          </button>
        </div>
      )}

      <ul className="space-y-3">
        {partners.map((partner) => (
          <li key={partner.partnerId} className="rounded-xl bg-white p-4 shadow-sm">
            <div className="flex items-baseline justify-between gap-3">
              <span className="truncate font-medium text-gray-800">{partner.fullName}</span>
              <span
                className="shrink-0 text-lg font-semibold tabular-nums text-green-800"
                aria-label={t('finance.partners.ownership')}
              >
                {partner.ownershipPercent}%
              </span>
            </div>

            {/* A bar, because "a quarter of the farm" is easier to read as a
                length than as a number — and four bars make an unequal split
                obvious at a glance. */}
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-gray-100">
              <div className="h-full bg-green-600" style={{ width: `${partner.ownershipPercent}%` }} />
            </div>

            <dl className="mt-3 grid grid-cols-3 gap-2 text-sm">
              <div>
                <dt className="text-xs text-gray-500">{t('finance.partners.capital')}</dt>
                <dd className="tabular-nums text-gray-800">{formatMoney(partner.capitalContributed)}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">{t('finance.partners.drawings')}</dt>
                <dd className="tabular-nums text-gray-800">{formatMoney(partner.drawings)}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">{t('finance.partners.net')}</dt>
                <dd
                  className={`tabular-nums ${isNegativeAmount(partner.netPosition) ? 'text-red-700' : 'text-gray-800'}`}
                >
                  {formatMoney(partner.netPosition)}
                </dd>
              </div>
            </dl>

            <button
              type="button"
              onClick={() => setOpenId(openId === partner.partnerId ? null : partner.partnerId)}
              aria-expanded={openId === partner.partnerId}
              className="mt-3 min-h-touch text-sm font-medium text-green-800 underline"
            >
              {t(openId === partner.partnerId ? 'partnerLedger.hide' : 'partnerLedger.show')}
            </button>

            {openId === partner.partnerId && <PartnerLedgerPanel partnerId={partner.partnerId} />}
          </li>
        ))}
      </ul>

      {partners.length > 0 && <p className="mt-4 text-xs text-gray-500">{t('finance.partners.drawingsNote')}</p>}
    </div>
  );
}
